import React, { useEffect, useState } from 'react';
import { getCharacterPortrait } from './portraits';
import './styles.scss';
import BrandSplash from '../BrandSplash/BrandSplash';

function discordAvatarUrl(discordId, discordAvatar) {
  if (!discordId || !discordAvatar) return null;
  const ext = discordAvatar.startsWith('a_') ? 'gif' : 'png';
  return `https://cdn.discordapp.com/avatars/${discordId}/${discordAvatar}.${ext}?size=64`;
}

const EMPTY_SLOT = { name: null, raceId: null, isFemale: null };

const activateOnKey = (action) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    action();
  }
};

const AuthPanel = ({ authState }) => {
  // Which slot's "Supprimer" confirmation is showing - null when none is.
  const [confirmingDeleteSlot, setConfirmingDeleteSlot] = useState(null);
  // Slot the player picked; while set, the window shows the connection screen
  // instead of the character list.
  const [launching, setLaunching] = useState(null);

  const failReason = authState ? authState.loginFailedReason : null;
  const comment = authState ? authState.comment : null;
  useEffect(() => {
    // Back to the list when the attempt fails or the client reports something
    // other than connection progress.
    if (failReason || (comment && !/^connexion/i.test(comment))) setLaunching(null);
  }, [failReason, comment]);

  if (!authState) return null;

  const send = (eventKey) => window.skyrimPlatform?.sendMessage?.(eventKey);
  const username = authState.discordUsername;
  const avatarUrl = discordAvatarUrl(authState.discordId, authState.discordAvatar);
  const initial = username ? username.charAt(0).toUpperCase() : '?';

  const slots = authState.characterSlots || [EMPTY_SLOT, EMPTY_SLOT];
  const slot0 = slots[0] || EMPTY_SLOT;
  const slot1 = slots[1] || EMPTY_SLOT;
  const slot1Unlocked = !!authState.slot1Unlocked;

  const handleDeleteClick = (slot) => (e) => {
    e.stopPropagation();
    setConfirmingDeleteSlot(slot);
  };

  const handleConfirmDelete = () => {
    const slot = confirmingDeleteSlot;
    setConfirmingDeleteSlot(null);
    send(`deleteCharacterEvent:${slot}`);
  };

  const message = authState.loginFailedReason || authState.comment;

  const launch = (index, data) => {
    setLaunching({ index, name: data ? data.name : null, raceId: data ? data.raceId : null, isFemale: data ? data.isFemale : null });
    send(`authAttemptEvent:${index}`);
  };

  const renderSlot = (index, data, unlocked) => {
    if (!unlocked) {
      const open = () => send('openPatreon');
      return (
        <div
          key={index}
          className="auth-panel__card auth-panel__card--empty auth-panel__card--locked"
          role="button"
          tabIndex={0}
          onClick={open}
          onKeyDown={activateOnKey(open)}
          title="Débloquer un deuxième personnage via Patreon"
        >
          <div className="auth-panel__empty">
            <span className="auth-panel__empty-title">Deuxième personnage</span>
            <span className="auth-panel__empty-sub">Réservé aux soutiens Patreon</span>
          </div>
        </div>
      );
    }

    if (!data.name) {
      const open = () => launch(index, null);
      return (
        <div
          key={index}
          className="auth-panel__card auth-panel__card--empty"
          role="button"
          tabIndex={0}
          onClick={open}
          onKeyDown={activateOnKey(open)}
          title="Créer un personnage"
        >
          <div className="auth-panel__empty">
            <span className="auth-panel__plus" aria-hidden="true" />
            <span className="auth-panel__empty-title">Nouveau personnage</span>
            <span className="auth-panel__empty-sub">Créer votre personnage</span>
          </div>
        </div>
      );
    }

    const name = data.name;
    const portrait = getCharacterPortrait(data.raceId, data.isFemale);
    const play = () => launch(index, data);
    const confirming = confirmingDeleteSlot === index;

    return (
      <div key={index} className="auth-panel__card">
        <div
          className="auth-panel__card-main"
          role="button"
          tabIndex={confirming ? -1 : 0}
          onClick={confirming ? undefined : play}
          onKeyDown={confirming ? undefined : activateOnKey(play)}
        >
          <div className="auth-panel__portrait">
            {portrait
              ? <img src={portrait} alt="" />
              : <span className="auth-panel__fallback">{name.charAt(0).toUpperCase()}</span>}
          </div>
          <div className="auth-panel__card-foot">
            <span className="auth-panel__card-name">{name}</span>
            <span className="auth-panel__card-play">Jouer</span>
          </div>
        </div>

        {!confirming && (
          <button
            type="button"
            className="auth-panel__delete"
            onClick={handleDeleteClick(index)}
          >
            Supprimer
          </button>
        )}

        {confirming && (
          <div className="auth-panel__confirm" role="alertdialog" aria-label="Confirmer la suppression">
            <div className="auth-panel__confirm-title">Supprimer {name} ?</div>
            <div className="auth-panel__confirm-text">Cette action est irréversible.</div>
            <div className="auth-panel__confirm-actions">
              <button
                type="button"
                className="auth-panel__button"
                onClick={() => setConfirmingDeleteSlot(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="auth-panel__button auth-panel__button--danger"
                onClick={handleConfirmDelete}
              >
                Supprimer
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!username) {
    return (
      <div className="auth-panel auth-panel--signin">
        <h1 className="auth-panel__title">Altherys</h1>
        <p className="auth-panel__lead">
          Connectez-vous avec votre compte Discord pour accéder à vos personnages.
        </p>
        <button
          type="button"
          className="auth-panel__button auth-panel__button--primary"
          onClick={() => send('openDiscordOauth')}
        >
          Se connecter avec Discord
        </button>
        {message && <div className="auth-panel__message">{message}</div>}
      </div>
    );
  }

  if (launching) {
    return <BrandSplash who={launching.name || 'Nouveau personnage'} status={comment} />;
  }

  return (
    <div className="auth-panel">
      <header className="auth-panel__head">
        <div>
          <h1 className="auth-panel__title">Vos personnages</h1>
          <p className="auth-panel__lead">Choisissez avec qui entrer dans le monde.</p>
        </div>
        <div className="auth-panel__identity">
          {avatarUrl
            ? <img className="auth-panel__avatar auth-panel__avatar--img" src={avatarUrl} alt="" />
            : <div className="auth-panel__avatar">{initial}</div>}
          <div className="auth-panel__username">{username}</div>
        </div>
      </header>

      <div className="auth-panel__characters">
        {renderSlot(0, slot0, true)}
        {renderSlot(1, slot1, slot1Unlocked)}
      </div>

      {message && <div className="auth-panel__message">{message}</div>}
    </div>
  );
};

export default AuthPanel;
